import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Plus, Trash2, FileText, AlertTriangle } from 'lucide-react';
import { packagesAPI } from '../lib/api';
import api from '../lib/api';
import { toast } from 'sonner';

const PURPOSE_OPTIONS = [
  { value: 'personal', label: 'Effets personnels' },
  { value: 'gift', label: 'Cadeau' },
  { value: 'commercial', label: 'Commercial / Vente' },
  { value: 'return', label: 'Retour de marchandise' },
  { value: 'sample', label: 'Échantillon' },
];

const COUNTRY_OPTIONS = [
  { code: 'FR', name: 'France' },
  { code: 'CN', name: 'Chine' },
  { code: 'US', name: 'États-Unis' },
  { code: 'DE', name: 'Allemagne' },
  { code: 'IT', name: 'Italie' },
  { code: 'ES', name: 'Espagne' },
  { code: 'GB', name: 'Royaume-Uni' },
  { code: 'TR', name: 'Turquie' },
  { code: 'IN', name: 'Inde' },
  { code: 'JP', name: 'Japon' },
  { code: 'KR', name: 'Corée du Sud' },
  { code: 'MA', name: 'Maroc' },
  { code: 'DZ', name: 'Algérie' },
  { code: 'TN', name: 'Tunisie' },
  { code: 'OTHER', name: 'Autre' },
];

export default function CustomsDeclarationForm({ packageId, groupId, packageDescription, onComplete, onCancel, existingDeclaration, mode = 'package' }) {
  const [items, setItems] = useState(
    existingDeclaration?.items?.length > 0
      ? existingDeclaration.items
      : [{ description: '', quantity: 1, value: 0, weight: 0.1, country_of_origin: 'FR' }]
  );
  const [purpose, setPurpose] = useState(existingDeclaration?.purpose || 'personal');
  const [loading, setLoading] = useState(false);

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, value: 0, weight: 0.1, country_of_origin: 'FR' }]);
  };

  const removeItem = (index) => {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    setItems(items.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const totalValue = items.reduce((sum, item) => sum + (item.value * item.quantity), 0);
  const totalWeight = items.reduce((sum, item) => sum + (item.weight * item.quantity), 0);

  const validate = () => {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.description.trim()) {
        toast.error(`Article ${i + 1} : la description est obligatoire`);
        return false;
      }
      if (item.quantity < 1) {
        toast.error(`Article ${i + 1} : la quantité doit être au moins 1`);
        return false;
      }
      if (item.value <= 0) {
        toast.error(`Article ${i + 1} : la valeur doit être supérieure à 0`);
        return false;
      }
      if (item.weight <= 0) {
        toast.error(`Article ${i + 1} : le poids doit être supérieur à 0`);
        return false;
      }
    }
    if (!purpose) {
      toast.error('Veuillez sélectionner le motif de l\'envoi');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    
    setLoading(true);
    try {
      const data = {
        items: items.map(item => ({
          description: item.description.trim(),
          quantity: parseInt(item.quantity),
          value: parseFloat(item.value),
          weight: parseFloat(item.weight),
          country_of_origin: item.country_of_origin
        })),
        total_value: parseFloat(totalValue.toFixed(2)),
        purpose
      };

      if (mode === 'group' && groupId) {
        await api.post(`/client/groups/${groupId}/customs-declaration`, data);
      } else {
        await packagesAPI.saveCustomsDeclaration(packageId, data);
      }
      toast.success('Déclaration douanière enregistrée');
      onComplete();
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      let msg;
      if (status === 404) {
        msg = 'Colis introuvable. Veuillez rafraîchir la page et réessayer.';
      } else if (status === 400) {
        msg = detail || 'Données invalides. Vérifiez votre formulaire.';
      } else {
        msg = detail || 'Erreur lors de l\'enregistrement. Veuillez réessayer.';
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="customs-declaration-form">
      {/* Header info */}
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-800">Déclaration douanière obligatoire</p>
          <p className="text-xs text-amber-600 mt-1">
            Déclarez le contenu de votre colis. Ces informations sont nécessaires pour le passage en douane.
            Toute fausse déclaration peut entraîner la saisie du colis.
          </p>
        </div>
      </div>

      {/* Purpose */}
      <div className="space-y-2">
        <Label className="font-medium">Motif de l'envoi *</Label>
        <Select value={purpose} onValueChange={setPurpose}>
          <SelectTrigger data-testid="customs-purpose-select">
            <SelectValue placeholder="Sélectionnez le motif" />
          </SelectTrigger>
          <SelectContent>
            {PURPOSE_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Items */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="font-medium">Contenu du colis *</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addItem}
            className="h-8 text-xs"
            data-testid="customs-add-item-btn"
          >
            <Plus className="w-3 h-3 mr-1" />
            Ajouter un article
          </Button>
        </div>

        {items.map((item, index) => (
          <div key={index} className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3" data-testid={`customs-item-${index}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Article {index + 1}</span>
              {items.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeItem(index)}
                  className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                  data-testid={`customs-remove-item-${index}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs text-slate-500">Description *</Label>
              <Input
                placeholder="Ex: Vêtements, Chaussures, Téléphone..."
                value={item.description}
                onChange={(e) => updateItem(index, 'description', e.target.value)}
                className="h-9"
                data-testid={`customs-item-desc-${index}`}
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Quantité *</Label>
                <Input
                  type="number"
                  min="1"
                  value={item.quantity}
                  onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                  className="h-9"
                  data-testid={`customs-item-qty-${index}`}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Valeur unitaire (EUR) *</Label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={item.value}
                  onChange={(e) => updateItem(index, 'value', parseFloat(e.target.value) || 0)}
                  className="h-9"
                  data-testid={`customs-item-value-${index}`}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Poids (kg) *</Label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={item.weight}
                  onChange={(e) => updateItem(index, 'weight', parseFloat(e.target.value) || 0)}
                  className="h-9"
                  data-testid={`customs-item-weight-${index}`}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Pays d'origine *</Label>
              <Select
                value={item.country_of_origin}
                onValueChange={(v) => updateItem(index, 'country_of_origin', v)}
              >
                <SelectTrigger className="h-9" data-testid={`customs-item-origin-${index}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRY_OPTIONS.map(c => (
                    <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
      </div>

      {/* Summary */}
      <div className="p-4 bg-slate-100 rounded-lg border border-slate-200">
        <h4 className="text-sm font-semibold text-slate-800 mb-2">Récapitulatif</h4>
        <div className="flex justify-between text-sm">
          <span className="text-slate-600">Nombre d'articles :</span>
          <span className="font-medium">{items.reduce((s, i) => s + (parseInt(i.quantity) || 0), 0)}</span>
        </div>
        <div className="flex justify-between text-sm mt-1">
          <span className="text-slate-600">Valeur totale déclarée :</span>
          <span className="font-medium">{totalValue.toFixed(2)} EUR</span>
        </div>
        <div className="flex justify-between text-sm mt-1">
          <span className="text-slate-600">Poids total déclaré :</span>
          <span className="font-medium">{totalWeight.toFixed(2)} kg</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <Button
          variant="outline"
          onClick={onCancel}
          className="flex-1"
          disabled={loading}
          data-testid="customs-cancel-btn"
        >
          Annuler
        </Button>
        <Button
          onClick={handleSubmit}
          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
          disabled={loading}
          data-testid="customs-submit-btn"
        >
          {loading ? 'Enregistrement...' : 'Valider la déclaration'}
        </Button>
      </div>
    </div>
  );
}
